import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getIsIndexDBInitialized } from 'app/slices/AppUiSlice';
import { getIsSyncInitialized } from 'app/slices/SyncManagerSlice';
import { createSyncRefreshCoordinator } from 'helpers/SyncRefreshCoordinator';
import { loadStoreFromWorker } from 'helpers/WorkerDataLoader';
import useSyncManager from 'hooks/useSyncManager';
import { useEffect } from 'react';
import { FirebaseCollections, SeasonID, Stores } from 'types';

// All consumers attached to one Redux store share request state, keyed by dataset and season.
const coordinators = new WeakMap<object, ReturnType<typeof createSyncRefreshCoordinator>>();

type SeasonDatasetRefreshOptions<T> = {
  /** IndexedDB store to read or refresh. */
  store: Stores;
  /** Existing SyncManager dataset status that signals completion. */
  collection: FirebaseCollections;
  /** Another existing completion signal whose writes also affect this dataset. */
  relatedCollection?: FirebaseCollections;
  /** Season requested by the caller; responses must be applied to this same season. */
  seasonID: SeasonID;
  /** Whether Redux already contains records for this dataset and season. */
  hasData: boolean;
  /** Explicit request to fetch Firestore, including retrying a previously failed version. */
  force?: boolean;
  /** Memoized callback that applies the response to its requested Redux scope. */
  onLoaded: (data: T[]) => void;
};

/**
 * Coordinates loading and refreshing a season dataset whose worker response is an array.
 * The caller owns the dataset's Redux update; this helper owns request eligibility and
 * worker completion. Server metadata comes from Redux's app-owned SyncManager listener.
 *
 * Timestamp selection:
 * Use the newest completion timestamp for collection (and relatedCollection, if supplied)
 * across the requested season's weeks. Compare it with this IndexedDB store's existing
 * local write timestamp. The coordinator also remembers handled and pending timestamps
 * in memory, so a local write cannot hide a newer announcement received during a request.
 * No persistent version fields or additional backend events are introduced.
 *
 * Request lifecycle:
 * 1. Wait for IndexedDB initialization, local sync initialization, and worker support.
 * 2. Deduplicate requests by store/season across consumers. Missing Redux data requests a
 *    cache read; the worker's interface fetches Firestore if that season is absent locally.
 * 3. Force Firestore when stale, explicitly requested, or handling a newer server version.
 *    Explicit reloads during an active load coalesce into one queued source fetch.
 * 4. Await the worker result, which is sent only after required IndexedDB writes complete.
 * 5. Refresh local sync metadata and invoke the caller's onLoaded.
 *    Only a successful, non-cancelled load acknowledges its triggering server timestamp.
 *
 * The existing IndexedDB interface controls fetching and cache fallback. Its response may
 * include other cached seasons; the caller selects records for the requested season.
 * SyncManager completion is treated as the backend's confirmation that data was written.
 * This helper does not add checks for missing backend data or change the interface contract.
 * Worker, parsing, and write failures are logged without invoking onLoaded. Failed versions
 * are not retried on rerender; a newer version or explicit force can retry.
 *
 * Consumers retain the shared coordinator while mounted. The final consumer's cleanup
 * aborts active workers and clears runtime tracking. Requests keep their original scope
 * across navigation; onLoaded must update that scope without changing the user's selection.
 *
 * @typeParam T - One record in the season snapshot returned by the worker.
 * @param {SeasonDatasetRefreshOptions<T>} options - Dataset mapping, requested season, cache presence, and result callback.
 * @return {boolean} Whether initialization and worker support permit a request. This is readiness,
 * not a loading or success indicator; callers can use it to consume a pending reload flag.
 * @see createSyncRefreshCoordinator for deduplication and pending-version handling.
 * @see loadStoreFromWorker for the worker protocol, rejection, and cancellation behavior.
 */
const useSeasonDatasetRefresh = <T>({
  store, collection, relatedCollection, seasonID, hasData, force = false, onLoaded,
}: SeasonDatasetRefreshOptions<T>) => {
  const dispatch = useAppDispatch();
  const dbReady = useAppSelector(getIsIndexDBInitialized);
  const syncReady = useAppSelector(getIsSyncInitialized);
  const { refreshSyncStatus } = useSyncManager();
  const serverVersion = useAppSelector((state) => Math.max(0,
    ...(state.sync.seasons.find((season) => season.seasonID === seasonID)?.weeks
      .map((week) => Math.max(week[collection]?.lastUpdatedOn || 0,
        relatedCollection ? week[relatedCollection]?.lastUpdatedOn || 0 : 0)) || [])
  ));
  const localVersion = useAppSelector((state) => state.sync.idbStatus[store]?.lastUpdatedOn || 0);
  let coordinator = coordinators.get(dispatch);
  if (!coordinator) {
    coordinator = createSyncRefreshCoordinator();
    coordinators.set(dispatch, coordinator);
  }
  const refresh = coordinator;
  const ready = dbReady && syncReady && !!seasonID && !!window.Worker;
  useEffect(() => refresh.retain(), [refresh]);
  useEffect(() => {
    if (!ready) return;
    refresh.request({
      key: `${store}:${seasonID}`,
      serverTimestamp: serverVersion,
      hasData,
      stale: serverVersion > localVersion,
      force,
      load: async (shouldFetch, signal) => {
        const data = await loadStoreFromWorker<T[]>(store, seasonID, shouldFetch, signal);
        if (signal.aborted) return;
        await refreshSyncStatus(seasonID);
        if (!signal.aborted) onLoaded(data);
      },
      onError: (error) => console.error(`Unable to load ${store} for ${seasonID}`, error),
    });
  }, [ready, seasonID, store, serverVersion, localVersion, hasData, force,
    onLoaded, refresh, refreshSyncStatus]);
  return ready;
};

export default useSeasonDatasetRefresh;
